import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject, Ref } from 'vue-property-decorator';

import EdCalendarCreateEvents
  from '@/core/components/ed-calendar/ed-calendar-create-events/ed-calendar-create-events.vue';
import {TOAST_TYPES} from '@/plugins/ed-toast/values/ed-toast.values';
import {Store, STORE} from '@/core/store/core.store';
import {CALENDAR_EVENTS} from '@/content/values/events';
import {_APP_STORE} from '@/core/enums/app.enum';
import { formatDate } from '@/core/services/calendar.util';
import { EdContextMenuModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { LOAD_CLASS_MESSAGES } from '@/core/values/app.values';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';

@Component
export default class EdCalendar extends Vue {

  /**
   * Splash
   */
  @Inject() EduacAppSetLoading;
  @Ref('wcCalendar') readonly wcCalendar: any;

  /* Data */
  public DEFAULT_CALENDAR_MODE = 'month';
  public CALENDAR_INITIAL_HOUR = 6;
  public CALENDAR_FINAL_HOUR = 22;
  public calendarList: any = [];
  public CALENDAR_TYPE_ORG : string = 'ORG';
  public CALENDAR_TYPE_CLASS : string = 'CLASS';
  public CALENDAR_TYPE_PRIVATE : string = 'PRIVATE';

  /* Hooks */
  created() {
    this.$edEventBus.$on(CALENDAR_EVENTS.CREATE_ITEM, this.onCreateItem);
    this.$edEventBus.$on(CALENDAR_EVENTS.UPDATE_ITEM, this.onUpdateItem);
    this.$edEventBus.$on(CALENDAR_EVENTS.REMOVE_ITEM, this.onRemoveItem);
    this.$edEventBus.$on(LOAD_CLASS_MESSAGES, this.loadClassMessages);
  }

  beforeDestroy() {
    this.$edEventBus.$off(CALENDAR_EVENTS.CREATE_ITEM);
    this.$edEventBus.$off(CALENDAR_EVENTS.UPDATE_ITEM);
    this.$edEventBus.$off(CALENDAR_EVENTS.REMOVE_ITEM);
    this.$edEventBus.$off(LOAD_CLASS_MESSAGES, this.loadClassMessages);
  }

  mounted() {
    this.getCalendars();
  }

  private getCalendars() {
    this.$store.dispatch(STORE.CALENDAR.ACTIONS.GET_CALENDARS)
      .then(() => {
        this.calendarList = this.calendars.map((item) => {
          if (item.info.calendarType === this.CALENDAR_TYPE_PRIVATE) {
            item.info.name = this.$t('calendar.data.myCalendar');
          }
          return { calendarId: item.info.calendarId, enable: false, name: item.info.name, calendarType: item.info.calendarType };
        });
      })
      .then(() => this.$store.dispatch(STORE.CALENDAR.ACTIONS.CLEAN_ITEMS))
      .then(() => {
        if (this.calendarList && this.calendarList.length > 0) {
          let orgCal = this.calendarList.find(d => d.calendarType == this.CALENDAR_TYPE_ORG);
          let privateCal = this.calendarList.find(d => d.calendarType == this.CALENDAR_TYPE_PRIVATE);

          if (orgCal || privateCal) {
            if (orgCal) orgCal.selected = true;
            if (privateCal) privateCal.selected = true;
            this.selectCalendars([privateCal?.calendarId, orgCal?.calendarId]);
            let _this = this;
            this.calendarList = this.calendarList.sort(function (a, b) {
              return _this.orderCal(a) - _this.orderCal(b);
            });
            if (this.wcCalendar?.setUserCalendars) {
              this.wcCalendar.setUserCalendars(this.calendarList,
                [privateCal?.calendarId, orgCal?.calendarId]);
            }
          }
        }
      });
  }

  private orderCal(value) {
    if(value.calendarType === this.CALENDAR_TYPE_ORG){
      return 2;
    } else if(value.calendarType === this.CALENDAR_TYPE_PRIVATE){
      return 1;
    } else if(value.calendarType === this.CALENDAR_TYPE_CLASS) {
      return 3;
    }
  }

  /* Computed */
  get calendars(): any[] {
    return Store.state.CalendarStore.calendars;
  }

  get items(){
    return Store.state.CalendarStore.items;
  }

  get isExpandCalendar(): boolean {
    return this.$store.state.AppStore.expandCalendar;
  }

  public createItem(start, end) {
    this.$EdModal.createModal(EdCalendarCreateEvents, {
      startDate: formatDate(start),
      endDate: formatDate(end)
    });
  }

  /**
   * Get event list from calendars selected within the given dates.
   * @param start Start date to filter the search.
   * @param end End date to filter the search.
   */
  private fillEventsAtCalendar(start, end) {
    const cals = this.calendarList
      .filter(cal => cal.enable == true)
      .map((item) => item.calendarId);

    if (cals.length > 0) {
      let params = { start: formatDate(start), end: formatDate(end), calendars: cals }
      this.$store.dispatch(STORE.CALENDAR.ACTIONS.GET_ITEMS, params).then(() => {
        this.updateCalendarEvents();
        this.EduacAppSetLoading(false);
      }).catch(() => {
        this.$EdToast.create({
          msg: `${this.$t('calendar.data.errorLoadingEvents')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
        this.EduacAppSetLoading(false);
      });
    } else {
      this.$store.dispatch(STORE.CALENDAR.ACTIONS.CLEAN_ITEMS);
      this.updateCalendarEvents();
      this.EduacAppSetLoading(false);
    }
  }

  /**
   * Updates the calendar events on the web component.
   * @private
   */
  private updateCalendarEvents() {
    if (this.wcCalendar?.setEvents) {
      this.wcCalendar.setEvents(this.items);
    }
  }

  /**
   * Handles the navigation event triggered from the Calendar Web Component.
   * @param customEvent Custom event dispatched with all the required data.
   */
  public onWcNavigationEvent(customEvent: CustomEvent) {

    const detail = customEvent.detail;
    if (detail.target === 'selectedCalendar') {
      this.selectCalendar(detail.value.calendarId, detail.value.checked);

    } else if (detail.target === 'createEvent') {
      this.createItem(detail.value.lastStartDate, detail.value.lastEndDate);

    } else if (detail.target === 'calendarDate') {
      this.fillEventsAtCalendar(detail.value.lastStartDate, detail.value.lastEndDate);

    } else if (detail.target === 'mode') {
      this.fillEventsAtCalendar(this.wcCalendar?.lastStartDate, this.wcCalendar?.lastEndDate);
    }
  }

  public onWcCalendarEventMouseOver(customEvent: CustomEvent): void {
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;
    const eventData: any = customEvent?.detail?.value?.eventData;
    if (mouseEvent && eventData) {
      this.$EdContextMenu.show(new EdContextMenuModel({
        position: { x: mouseEvent.x, y: mouseEvent.y },
        options: [{ text: eventData.summary }],
        darkMode: true
      }));
    }
  }

  public onWcCalendarEventMouseLeave(): void {
    this.$EdContextMenu.close();
  }

  private selectCalendar(item: string, checked: boolean = true){
    const calItem = this.calendarList.find(c => c.calendarId === item);
    if (calItem) {
      calItem.enable = checked;
    }
    this.reloadCalendarItems();
  }

  private selectCalendars(items: string[], checked: boolean = true){
    let foundItems: any[] = [];
    let notFoundItems: string[] = [];
    
    items.forEach(item => {
      let calItem: any = this.calendarList.find(c=> c.calendarId == item);
      if (calItem) {
        calItem.enable = checked;
        foundItems.push(item);
      } else {
        notFoundItems.push(item);
      }
    });

    if (foundItems.length > 0) {
      if (!checked) {
        const removePromises = foundItems.map(item => 
          this.$store.dispatch(STORE.CALENDAR.ACTIONS.REMOVE_ITEMS, item)
        );
        Promise.all(removePromises).then(() => this.updateCalendarEvents());
      } else {
        this.reloadCalendarItems();
      }
    } else {
      this.reloadCalendarItems();
    }
  }

  public reloadCalendarItems(){
    try{
      this.EduacAppSetLoading(true);
      this.fillEventsAtCalendar(this.wcCalendar?.lastStartDate, this.wcCalendar?.lastEndDate);
    } catch (e) {
      this.EduacAppSetLoading(false);
    }
  }

  /**
   * Handles the event info event triggered from the Calendar Web Component.
   * @param customEvent Custom event dispatched with all the required data.
   */
  public onWcCalendarEventInfo(customEvent: CustomEvent) {
    this.$EdModal.createModal(EdCalendarCreateEvents, {
      startDate: formatDate(this.wcCalendar?.lastStartDate),
      endDate: formatDate(this.wcCalendar?.lastEndDate),
      item: customEvent.detail.value
    });
  }

  /**
   * Handles any item changes in the calendar.
   * @param item Item that has been changed.
   * @param toastMessage Message to show in the toast.
   * @private
   */
  private onItemChange(item: any, toastMessage: string) {
    this.updateCalendarEvents();
    this.EduacAppSetLoading(false);
    this.showToast(toastMessage, item.summary);
  }

  private onCreateItem(item) {
    this.onItemChange(item, 'calendar.data.eventCreated');
  }

  private onUpdateItem(item) {
    this.onItemChange(item, 'calendar.data.eventUpdated');
  }

  private onRemoveItem(item: any) {
    this.onItemChange(item, 'calendar.data.eventRemoved');
  }

  private showToast(label:string, summary:string){
    this.$EdToast.create({
      msg:  `${this.$t(label, { eventName: summary })}`,
      type: TOAST_TYPES.SUCCESS,
      top: '3rem',
      timeout: 3000
    });
  }

  public expandCalendar(){
    Store.commit(_APP_STORE.SET_EXPAND_CALENDAR, !this.$store.state.AppStore.expandCalendar);
  }

  private loadClassMessages(message : WebSocketMessageResponse) {
    this.getCalendars();
  }
}
